import { z } from 'zod';
import { Money } from './money';

/** Сводка смены для закрытия: касса показывает, сервер считает. */
export const ShiftSummary = z.object({
  shiftId: z.string(),
  openedAt: z.string().datetime(),
  openingCash: Money,
  checks: z.number().int(),
  cashSales: Money,
  cardSales: Money,
  qrSales: Money,
  /** размен + наличные продажи — столько должно лежать в ящике */
  expectedCash: Money,
  /** хиты смены — кассир закрывает день с чувством дня */
  topDishes: z.array(z.object({ name: z.string(), qty: z.number() })).optional(),
  /** «на чай картой» за смену — деньги официантов, не выручка */
  tipsCard: Money.optional(),
});
export type ShiftSummary = z.infer<typeof ShiftSummary>;

export const FiscalState = z.enum(['QUEUED', 'SENT', 'ERROR', 'OFF']);
export type FiscalState = z.infer<typeof FiscalState>;

export const ShiftOrderRow = z.object({
  orderId: z.string(),
  number: z.number().int(),
  closedAt: z.string().datetime(),
  total: Money,
  status: z.enum(['CLOSED', 'CANCELLED']),
  methods: z.array(z.enum(['CASH', 'CARD', 'KASPI_QR'])),
  tableName: z.string().nullable(),
  fiscal: FiscalState,
});
export type ShiftOrderRow = z.infer<typeof ShiftOrderRow>;

export const ShiftOrders = z.object({ orders: z.array(ShiftOrderRow) });
export type ShiftOrders = z.infer<typeof ShiftOrders>;

/** Полный чек — для копии с кассы. */
export const OrderDetail = z.object({
  orderId: z.string(),
  number: z.number().int(),
  closedAt: z.string().datetime(),
  cashierName: z.string(),
  tableName: z.string().nullable(),
  items: z.array(z.object({
    name: z.string(),
    qty: z.number(),
    unitPrice: Money,
    modifiers: z.array(z.object({ name: z.string(), priceDelta: Money })),
  })),
  total: Money,
  payments: z.array(z.object({
    method: z.enum(['CASH', 'CARD', 'KASPI_QR']),
    amount: Money,
    tenderedCash: Money.nullable(),
  })),
  fiscal: z.object({ status: FiscalState, qrUrl: z.string().nullable() }),
});
export type OrderDetail = z.infer<typeof OrderDetail>;

/* ── Отчётность смены и операционного дня ─────────────────────────
   Один агрегат кормит три печатные формы: X-отчёт, Z-отчёт и Акт
   реализации. Собирается сервером за один запрос — касса и офис
   только раскладывают его на ленту или экран. */

export const ShiftBrief = z.object({
  id: z.string(),
  number: z.number().int(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  openedByName: z.string(),
  checks: z.number().int(),
  revenue: z.number().int(),
});
export type ShiftBrief = z.infer<typeof ShiftBrief>;

export const ReportStationBlock = z.object({
  station: z.enum(['KITCHEN', 'BAR', 'OTHER']),
  categories: z.array(z.object({
    name: z.string(),
    items: z.array(z.object({
      name: z.string(),
      /** артикул товара — печатается перед именем, как у больших */
      sku: z.string().nullable(),
      qty: z.number(),
      sum: z.number().int(),
    })),
    sum: z.number().int(),
  })),
  lines: z.number().int(),
  sum: z.number().int(),
});
export type ReportStationBlock = z.infer<typeof ReportStationBlock>;

export const ShiftReport = z.object({
  /** смена (для X/Z) или операционный день (для акта за день) */
  kind: z.enum(['SHIFT', 'DAY']),
  shiftId: z.string().nullable(),
  shiftNumber: z.number().int().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  openedByName: z.string().nullable(),
  periodFrom: z.string(),
  periodTo: z.string(),

  checks: z.number().int(),
  /** строк-позиций продано за период */
  itemsCount: z.number().int(),
  avgCheck: z.number().int(),
  revenue: z.number().int(),
  discounts: z.number().int(),
  /** чеков со скидкой — контроль злоупотреблений одним взглядом */
  discountChecks: z.number().int(),
  /** скидки по видам из снапшота акций заказа */
  promoKinds: z.array(z.object({
    name: z.string(),
    checks: z.number().int(),
    sum: z.number().int(),
  })),
  service: z.number().int(),
  payments: z.array(z.object({
    method: z.enum(['CASH', 'CARD', 'KASPI_QR']),
    amount: z.number().int(),
  })),
  tips: z.number().int(),

  openingCash: z.number().int(),
  cashIn: z.number().int(),
  cashOut: z.number().int(),
  cashSales: z.number().int(),
  expectedCash: z.number().int(),

  byWaiter: z.array(z.object({
    name: z.string(),
    checks: z.number().int(),
    sum: z.number().int(),
  })),
  voids: z.object({
    count: z.number().int(),
    sum: z.number().int(),
    lines: z.array(z.object({
      name: z.string(),
      qty: z.number().nullable(),
      sum: z.number().nullable(),
      reason: z.string(),
    })),
  }),
  hits: z.array(z.object({ name: z.string(), qty: z.number() })),
  stations: z.array(ReportStationBlock),
  /** смена пересекла границу дня — раскладка по оперденям */
  days: z.array(z.object({
    date: z.string(),
    checks: z.number().int(),
    revenue: z.number().int(),
  })).optional(),
  cancelled: z.number().int(),
  /** день пересекает незакрытая смена: в ящике деньги прошлых дней */
  crossDayOpenShift: z.boolean().optional(),
});
export type ShiftReport = z.infer<typeof ShiftReport>;

/* ── Офис: смены точки, дистанционное закрытие с пересчётом ────── */
export const OfficeShiftRow = z.object({
  id: z.string(),
  number: z.number().int(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  openedByName: z.string(),
  checks: z.number().int(),
  revenue: z.number().int(),
  /** для открытых: сколько должно быть в ящике по расчёту */
  expectedCash: z.number().int().nullable(),
  /** для закрытых: что насчитали фактически (null — без пересчёта) */
  closingCash: z.number().int().nullable(),
  /** разрез по оперднях, когда смена пересекает границу 06:00 */
  days: z.array(z.object({
    date: z.string(),
    checks: z.number().int(),
    revenue: z.number().int(),
  })).optional(),
});
export type OfficeShiftRow = z.infer<typeof OfficeShiftRow>;

export const OfficeCloseShift = z.object({
  /** фактическая наличность в ящике, тиын */
  actualCash: z.number().int().nonnegative(),
});
export type OfficeCloseShift = z.infer<typeof OfficeCloseShift>;

/** Текущая смена терминала — касса сверяется с сервером. */
export const CurrentShift = z.union([
  z.object({ open: z.literal(false) }),
  z.object({
    open: z.literal(true),
    id: z.string(),
    openedAt: z.string(),
    openedById: z.string(),
    openedByName: z.string(),
    openingCash: z.number().int(),
    checks: z.number().int(),
    revenue: z.number().int(),
  }),
]);
export type CurrentShift = z.infer<typeof CurrentShift>;
